<script setup lang="ts">
import { ref, onMounted, watch, toRaw } from 'vue';
import { CommonUtils } from '@/utils/commonUtils';
import { useSystemConfigStoreHook } from '@/store/modules/systemConfig';
import languageSelect from "@/components/common/languageSelect.vue";
import { useRoute } from 'vue-router';
import { newRouterGroupApi } from '@/api/new-router-group/new-router-group-api';
import  { constantRoutes } from '@/router/modules';
import Products from './Products.vue';
import { useRenderIcon } from '@/components/ReIcon/src/hooks'
import { isAllEmpty } from '@pureadmin/utils';
import { checkAndNavigate } from '@/utils/redirect';
import ArrowUp from '@/assets/svg/navbar/arrow_up.svg';
import ArrowDown from '@/assets/svg/navbar/arrow_down.svg';
const route = useRoute();
const sysStore = useSystemConfigStoreHook();
const navList = ref<any[]>([]);
const currentIndex = ref(0);
const isDropdownVisible = ref(false);
const activeChildPath = ref('');

// 判断导航项是否激活
const isNavActive = (item: any) => {
  // 如果是有子菜单的父路由
  if (item.isMore) {
    // 如果当前路由路径是子路由之一，则激活
    return item.children?.some((child: any) => child.path === route.path);
  }
  // 如果是没有子菜单的父路由，直接比较路径
  return route.path === item.path || route.path.startsWith(item.path + '/');
};

// 如果 logoBigUrl 存在才显示 logo
const showLogo = ref(!!sysStore.commonConfig.logoBigUrl);

const handleClick = (item: any, index: number) => {
  currentIndex.value = index;
  // 如果有两个或者两个以上的子菜单，不进行路由跳转，而是打开下拉菜单
  if (item.isMore) {
    isDropdownVisible.value = !isDropdownVisible.value;
    return;
  }
  // 没有子菜单的情况下，进行路由跳转
  isDropdownVisible.value = false;
  if (isAllEmpty(item.path)) {
    return;
  }
  if (item.path.startsWith('http')) {
    window.open(item.path, '_blank');
  } else {
    checkAndNavigate(item.path);
  }
};
const syncCurrentByPath = (path: string) => {
  const idx = navList.value.findIndex(item =>
    path === item.path || path.startsWith(item.path + '/')
  );
  if (idx !== -1) {
    currentIndex.value = idx;
  } else {
    currentIndex.value = 0;
  }
  isDropdownVisible.value = false;
  activeChildPath.value = path;
};
const loadNavList = async () => {
  const { data: dynamicRoutesResp } = await newRouterGroupApi.getNewRouterGroup();
  const dynamicRoutes = Array.isArray(dynamicRoutesResp) ? dynamicRoutesResp : [];
  const formattedDynamicRoutes = dynamicRoutes
    .map((item: any) => ({
      name: item.name,
      icon: item.icon,
      path: item.path,
      rank: item.rank,
      isMore: Array.isArray(item.routers) && item.routers.length > 1,
      children: item.routers || [],
    }));
  // 从 constantRoutes 中提取所有 children 并格式化为导航项
  const staticRoutes = constantRoutes.flatMap(route =>
    (route.children || [])
      .filter(child => child.meta?.isShowLink !== false)  // 关键过滤条件
      .map(child => ({
        name: child.meta?.title,
        icon: child.meta?.icon,
        path: child.path,
        rank: child.meta?.rank,
        isMore: Array.isArray(child.children) && child.children.length > 1,
        children: (child.children || []).filter(grandChild => grandChild.meta?.isShowLink !== false), // 同时过滤子路由
      }))
  );
  // 合并路由并按 rank 排序
  navList.value = [...staticRoutes, ...formattedDynamicRoutes];
  syncCurrentByPath(route.path);
};
onMounted(() => {
  loadNavList();
});
watch(() => route.path, newPath => {
  syncCurrentByPath(newPath);
});
const handleMouseEnter = (item: any, index: number) => {
  // 如果鼠标从一个父路由移动到另一个父路由
  if (isDropdownVisible.value && currentIndex.value !== index) {
    // 先关闭当前菜单
    isDropdownVisible.value = false;

    // 使用setTimeout确保关闭动画有时间执行
    setTimeout(() => {
      // 如果新项目有子菜单，再打开新菜单
      if (item.isMore) {
        currentIndex.value = index;
        isDropdownVisible.value = true;
      }
    }, 100); // 短暂延迟，避免闪烁
  }
  // 如果当前没有显示下拉菜单
  else if (item.isMore) {
    isDropdownVisible.value = true;
    currentIndex.value = index;
  }
};

const handleMouseLeave = () => {
  isDropdownVisible.value = false;
};
const handleDropdownEnter = () => {
  // 保持下拉菜单显示状态
  isDropdownVisible.value = true;
};
</script>

<template>
  <div class="w-full h-[80px] cursor-pointer relative" @mouseleave="handleMouseLeave">
    <div class="max-width mx-auto  h-full flex items-center">
      <div class="flex items-center justify-between h-full w-full">
        <div v-if="showLogo" class="flex">
          <img class="h-[2.46rem]" :src="sysStore.commonConfig.logoBigUrl" />
        </div>
        <div class="flex flex-1 mx-auto h-full items-center justify-start">
          <div class="px-[35px]  h-[80px] flex items-center" v-for="(item, index) in navList" :key="index"
            @click="handleClick(item, index)" @mouseenter="handleMouseEnter(item, index)">
            <div :class="isNavActive(item) ? 'text-main-primary' : ''"
              class="flex hover:text-main-primary transition-colors duration-300 items-center">
              <div v-if="toRaw(item?.icon)" class="mr-[0.5rem]">
                <component :is="useRenderIcon(
                  toRaw(item?.icon)
                )
                  " />
              </div>
              <span>
                {{ CommonUtils.getTranslateText(item.name, sysStore.currentLang) }}
              </span>
              <div class="ml-[12px]" v-if="item.isMore">
                <ArrowUp class="text-main-primary w-[8px] h-[6px]"
                  v-if="isDropdownVisible && currentIndex === index" />
                <ArrowDown v-else class="w-[8px] h-[6px]"
                  :class="isNavActive(item) ? 'text-main-primary' : ''" />
              </div>
            </div>
          </div>
        </div>
        <div class="flex h-full">
          <languageSelect @updateLang="loadNavList"></languageSelect>
        </div>
      </div>
    </div>
    <transition name="dropdown-container">
      <div class="dropdown-menu absolute top-[80px] left-0 w-full h-auto bg-white z-[999] overflow-hidden"
        v-if="isDropdownVisible && navList[currentIndex].isMore" @mouseenter="handleDropdownEnter">
        <div class="max-width mx-auto h-auto flex items-center min-h-[100px]">
          <transition name="dropdown-content" mode="out-in">
            <Products :products="navList[currentIndex].children" :active-path="route.path" />
          </transition>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped>
.nav-bar {
  transition: all 0.3s ease;
}

.nav-bar:hover {
  z-index: 10;
  box-shadow: 0 8px 16px rgba(55, 99, 170, 0.1);
  transform: translateY(-2px);
}

.nav-item {
  transition: all 0.2s ease;
  position: relative;
}

.nav-item:hover {
  box-shadow: 0 2px 8px rgba(55, 99, 170, 0.1);
}

.dropdown-menu {
  border-top: 1px solid #f0f0f0;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
}

/* 容器动画: 从上到下展开 */
.dropdown-container-enter-active,
.dropdown-container-leave-active {
  transition: all 0.4s cubic-bezier(0.215, 0.61, 0.355, 1);
  max-height: 400px;
}

.dropdown-container-enter-from,
.dropdown-container-leave-to {
  opacity: 0;
  max-height: 0;
}

/* 内容动画: 淡入淡出 */
.dropdown-content-enter-active {
  transition: all 0.4s cubic-bezier(0.215, 0.61, 0.355, 1) 0.1s;
}

.dropdown-content-leave-active {
  transition: all 0.2s cubic-bezier(0.215, 0.61, 0.355, 1);
}

.dropdown-content-enter-from,
.dropdown-content-leave-to {
  opacity: 0;
  transform: translateY(-15px);
}

.dropdown-content-enter-to,
.dropdown-content-leave-from {
  opacity: 1;
  transform: translateY(0);
}

/* 导航按钮悬停动画 */
.nav-btn {
  position: relative;
  transition: all 0.3s ease;
}

.nav-btn::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 0;
  height: 2px;
  background-color: var(--el-color-primary, #1890ff);
  transition: all 0.3s ease;
  transform: translateX(-50%);
}

.nav-btn:hover {
  transform: translateY(-2px);
}

.nav-btn:hover::after {
  width: 50%;
}

.sidebar-title {
  display: inline-block;
  height: 32px;
  margin: 2px 0 0 12px;
  overflow: hidden;
  font-size: 18px;
  font-weight: 600;
  line-height: 32px;
  color: var(--el-color-primary);
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
